/**
 * StackFormDialog — bám đúng Access spec `frmFactoryQryStackAdd`
 * (`plan/access_all/form_frmFactoryQryStackAdd.txt`).
 *
 * Visible user-editable fields (theo TabIndex Access):
 *   1. stack_id (required, disabled trong edit mode — là PK)
 *   2. stack_area (ComboBox, source `public_factory_area`)
 *   3. note (multiline, spans 2 rows trong Access)
 *   4. warehouse (default từ factory_overview.receive_warehouse)
 *   5. is_finished (ComboBox Yes/No, default No)
 *   6. is_received_from_po (ComboBox Yes/No, default No)
 *
 * Hidden system fields KHÔNG render: id, id2, is_status, created_*, updated_*.
 *
 * Validation (theo VBA `btnSave_Click`):
 *   - stack_id required (strCheck = ",stack_id,")
 *   - Trong NEW mode: check duplicate stack_id (DCount qua public_v_factory_stack_all).
 *     Frontend dùng `existingRows` để pre-check; backend vẫn validate authoritative.
 *
 * Layout (theo Top/Left twips):
 *   Row 1: Stack ID* | Note (rowspan)
 *   Row 2: Stack Area | (note tiếp)
 *   Row 3: Warehouse  | (note tiếp)
 *   Row 4: Is Finished | Is Received From PO
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import type {
  FactoryCell,
  FactoryBRowPayload,
} from '@/store/api/factoryApi'

type StackRow = { id: string | number } & Record<string, FactoryCell>

interface DraftState {
  stack_id: string
  stack_area: string
  warehouse: string
  note: string
  is_finished: string
  is_received_from_po: string
}

const EMPTY_DRAFT: DraftState = {
  stack_id: '',
  stack_area: '',
  warehouse: '',
  note: '',
  is_finished: 'No',
  is_received_from_po: 'No',
}

const YES_NO_OPTIONS: { value: 'No' | 'Yes' }[] = [{ value: 'No' }, { value: 'Yes' }]

const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

const compactInputSx = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}

// Skill `/convert-form` readonly bg standard: neutral gray `#f1f3f5` (light) /
// `rgba(255,255,255,0.06)` (dark) thay MUI default disabled (phớt hồng trên
// theme TLC cream). Áp dụng cho field `Enabled = NotDefault` Access (stack_id
// trong edit, warehouse auto-fill).
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: 'text.secondary',
    fontWeight: 600,
  },
}

/** Lấy giá trị warehouse phổ biến nhất trong rows hiện có — fallback cho default
 *  khi BE chưa expose `factory_overview.receive_warehouse` qua context. */
function deriveDefaultWarehouse(rows: StackRow[]): string {
  if (!rows.length) return ''
  const counts = new Map<string, number>()
  for (const r of rows) {
    const v = String(r.warehouse ?? '').trim()
    if (!v) continue
    counts.set(v, (counts.get(v) ?? 0) + 1)
  }
  if (!counts.size) return ''
  let best = ''
  let bestN = 0
  for (const [k, n] of counts) {
    if (n > bestN) {
      best = k
      bestN = n
    }
  }
  return best
}

interface StackFormDialogProps {
  open: boolean
  mode: 'new' | 'edit'
  /** Row hiện tại (khi edit). null khi new. */
  row: StackRow | null
  saving: boolean
  /** Toàn bộ rows hiện có — dùng cho stack_area options + duplicate check + warehouse default. */
  existingRows: StackRow[]
  /** Permission gating — false → ẩn Save + show read-only banner (defensive layer). */
  canEdit?: boolean
  onClose: () => void
  onSave: (payload: FactoryBRowPayload) => void
}

export function StackFormDialog({
  open,
  mode,
  row,
  saving,
  existingRows,
  canEdit = true,
  onClose,
  onSave,
}: StackFormDialogProps) {
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)

  /** Hydrate draft khi dialog mở (new → defaults, edit → row values). */
  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && row) {
      setDraft({
        stack_id: String(row.stack_id ?? ''),
        stack_area: String(row.stack_area ?? ''),
        warehouse: String(row.warehouse ?? ''),
        note: String(row.note ?? ''),
        is_finished: normalizeYn(row.is_finished),
        is_received_from_po: normalizeYn(row.is_received_from_po),
      })
    } else {
      setDraft({
        ...EMPTY_DRAFT,
        warehouse: deriveDefaultWarehouse(existingRows),
      })
    }
  }, [open, mode, row, existingRows])

  /** Stack area options — distinct values từ existing rows, sort vi locale. */
  const areaOptions = useMemo(() => {
    const set = new Set<string>()
    for (const r of existingRows) {
      const v = String(r.stack_area ?? '').trim()
      if (v) set.add(v)
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'vi'))
  }, [existingRows])

  /** Duplicate check (parity VBA: DCount qua `public_v_factory_stack_all`).
   *  Chỉ pre-check ở frontend — BE vẫn authoritative. */
  const duplicateStackId = useMemo(() => {
    if (mode !== 'new') return false
    const id = draft.stack_id.trim()
    if (!id) return false
    return existingRows.some((r) => String(r.stack_id ?? '').trim() === id)
  }, [draft.stack_id, existingRows, mode])

  const stackIdEmpty = draft.stack_id.trim().length === 0
  const canSave = canEdit && !stackIdEmpty && !duplicateStackId && !saving

  const handleSave = () => {
    if (!canSave) return
    /** Payload — Access spec line 1544/1545:
     *    Update: warehouse,is_finished,note,is_received_from_po,stack_area
     *    Insert: stack_id,warehouse,is_finished,note,is_received_from_po,stack_area */
    const payload: FactoryBRowPayload = {
      stack_id: draft.stack_id.trim(),
      stack_area: draft.stack_area.trim() || null,
      warehouse: draft.warehouse.trim() || null,
      note: draft.note.trim() || null,
      is_finished: draft.is_finished,
      is_received_from_po: draft.is_received_from_po,
    }
    onSave(payload)
  }

  const handleChange = <K extends keyof DraftState>(key: K, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(720px, 90vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          component="span"
          sx={{ flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {mode === 'new' ? 'New' : 'Edit'} — Factory · Stack
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={!canSave}
            sx={titleBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={saving}
            sx={titleBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          ...compactInputSx,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          pt: 1.5,
        }}
      >
        {!canEdit ? (
          <Alert severity="info" sx={{ mb: 1.25, py: 0.25 }}>
            Read-only — you don't have edit permission for this factory.
          </Alert>
        ) : null}
        {duplicateStackId ? (
          <Alert severity="error" sx={{ mb: 1.25, py: 0.25 }}>
            This Stack ID already exists — please use a different code.
          </Alert>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            columnGap: 1.5,
            rowGap: 1.25,
            alignItems: 'start',
          }}
        >
          {/* ── Col 1 (rows 1-4) ─────────────────────────────────── */}
          <Stack spacing={1.25}>
            <TextField
              label="Stack ID"
              size="small"
              fullWidth
              required
              autoFocus={mode === 'new'}
              value={draft.stack_id}
              onChange={(e) => handleChange('stack_id', e.target.value)}
              disabled={mode === 'edit'}
              error={!stackIdEmpty && duplicateStackId}
              helperText={
                mode === 'new'
                  ? 'Stack code must be unique.'
                  : 'Stack ID cannot be changed after creation.'
              }
              sx={mode === 'edit' ? readonlyFieldSx : undefined}
            />
            <Autocomplete<string, false, false, true>
              freeSolo
              size="small"
              options={areaOptions}
              value={draft.stack_area}
              onChange={(_, v) => handleChange('stack_area', String(v ?? ''))}
              onInputChange={(_, v) => handleChange('stack_area', v)}
              renderInput={(params) => (
                <TextField {...params} label="Stack Area" fullWidth />
              )}
            />
            <TextField
              label="Warehouse"
              size="small"
              fullWidth
              value={draft.warehouse}
              disabled
              sx={readonlyFieldSx}
              // helperText="Lấy mặc định từ factory_overview.receive_warehouse — không cho sửa"
            />
            <Autocomplete
              size="small"
              fullWidth
              options={YES_NO_OPTIONS}
              value={YES_NO_OPTIONS.find((o) => o.value === draft.is_finished) ?? null}
              onChange={(_, v) => {
                if (!v) return
                handleChange('is_finished', v.value)
              }}
              getOptionLabel={(o) => (o ? o.value : '')}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderInput={(params) => (
                <TextField {...params} label="Is Finished" />
              )}
            />
          </Stack>

          {/* ── Col 2 (note + is_received_from_po) ──────────────── */}
          <Stack spacing={1.25}>
            <TextField
              label="Note"
              size="small"
              fullWidth
              multiline
              rows={6}
              value={draft.note}
              onChange={(e) => handleChange('note', e.target.value)}
            />
            <Autocomplete
              size="small"
              fullWidth
              options={YES_NO_OPTIONS}
              value={
                YES_NO_OPTIONS.find((o) => o.value === draft.is_received_from_po) ?? null
              }
              onChange={(_, v) => {
                if (!v) return
                handleChange('is_received_from_po', v.value)
              }}
              getOptionLabel={(o) => (o ? o.value : '')}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderInput={(params) => (
                <TextField {...params} label="Is Received From PO" />
              )}
            />
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

/** Convert FactoryCell → "Yes"/"No"/raw. Access dùng "0"/"1" hoặc "No"/"Yes";
 *  view trả về text "No"/"Yes" theo example data. */
function normalizeYn(v: FactoryCell): string {
  if (v == null) return 'No'
  const s = String(v).trim()
  if (!s) return 'No'
  if (s === '1' || s.toLowerCase() === 'yes' || s.toLowerCase() === 'true') return 'Yes'
  if (s === '0' || s.toLowerCase() === 'no' || s.toLowerCase() === 'false') return 'No'
  return s
}
